"use client";

import React from "react";
import { motion } from "framer-motion";
import { Brain, Sparkles, Zap } from "lucide-react";

interface AIFeature {
  title: string;
  description: string;
  bulletPoints: string[];
}

interface AIEdgeSectionProps {
  features: AIFeature[];
  accentColor?: string;
  title?: string;
  description?: string;
}

const AI_ICONS = [Brain, Sparkles, Zap];

export default function AIEdgeSection({
  features,
  accentColor = "#2C78B9",
  title = "Smarter Operations with Built-In Artificial Intelligence",
  description = "Leverage predictive analytics, machine learning, and intelligent automation to stay ahead of the competition.",
}: AIEdgeSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-slate-50/50 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#071428] via-[#0d2347] to-[#0a1628]" />

      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `radial-gradient(${accentColor} 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${accentColor}15, transparent 70%)`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-blue-300">
              AI Edge
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-4 leading-tight"
          >
            {title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed font-medium"
          >
            {description}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, idx) => {
            const IconComponent = AI_ICONS[idx % AI_ICONS.length];

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -8 }}
                className="group relative flex flex-col p-6 lg:p-8 rounded-[32px] bg-white/5 border border-white/10 backdrop-blur-sm shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] transition-all duration-500 hover:bg-white/[0.08] hover:border-white/20"
              >
                <div className="absolute inset-0 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(circle at top right, ${accentColor}15, transparent 70%)` }}
                />

                <div className="relative">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
                    style={{ backgroundColor: `${accentColor}25` }}
                  >
                    <IconComponent
                      className="w-6 h-6"
                      style={{ color: accentColor }}
                      strokeWidth={2}
                    />
                  </div>

                  <h3 className="text-xl font-extrabold text-white mb-3 tracking-tight leading-tight">
                    {feature.title}
                  </h3>

                  <p className="text-sm font-medium text-slate-400 leading-relaxed mb-5">
                    {feature.description}
                  </p>

                  <ul className="space-y-2.5">
                    {feature.bulletPoints.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2.5">
                        <span
                          className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: accentColor }}
                        />
                        <span className="text-[13px] font-semibold text-slate-400 leading-relaxed">
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
